import {
  Users,
  Dumbbell,
  Activity,
  Calendar,
  UserCheck,
  BookOpen,
  UtensilsCrossed,
  CreditCard,
  Settings,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Sparkles,
} from "lucide-react";

export default function AdminCommandCenter({
  stats,
  actionItems,
  onNavigate,
}) {
  const {
    totalMembers = 0,
    activeTrainers = 0,
    workoutSessions = 0,
    upcomingAppointments = 0,
    isLoadingStats = false,
  } = stats || {};

  const totalActionCount = actionItems.reduce((acc, item) => acc + item.count, 0);

  const PLATFORM_TILES = [
    {
      id: "members",
      title: "Members",
      desc: "Accounts, tiers & roles",
      icon: Users,
      color: "from-sky-500/20 to-blue-600/10 text-sky-400 border-sky-500/30",
    },
    {
      id: "trainers",
      title: "Trainers",
      desc: "Profiles, approvals & hours",
      icon: UserCheck,
      color: "from-emerald-500/20 to-teal-600/10 text-emerald-400 border-emerald-500/30",
    },
    {
      id: "programs",
      title: "Workout Plans",
      desc: "Programs & templates",
      icon: Dumbbell,
      color: "from-purple-500/20 to-indigo-600/10 text-purple-400 border-purple-500/30",
    },
    {
      id: "exercises",
      title: "Exercise Library",
      desc: "Media, muscles & form",
      icon: BookOpen,
      color: "from-amber-500/20 to-orange-600/10 text-amber-400 border-amber-500/30",
    },
    {
      id: "nutrition",
      title: "Nutrition",
      desc: "Meal plans & macro targets",
      icon: UtensilsCrossed,
      color: "from-emerald-500/20 to-green-600/10 text-emerald-400 border-emerald-500/30",
    },
    {
      id: "sessions",
      title: "Appointments",
      desc: "Bookings & scheduling",
      icon: Calendar,
      color: "from-cyan-500/20 to-sky-600/10 text-cyan-400 border-cyan-500/30",
    },
    {
      id: "subscriptions",
      title: "Subscriptions",
      desc: "Membership tiers & access",
      icon: CreditCard,
      color: "from-rose-500/20 to-pink-600/10 text-rose-400 border-rose-500/30",
    },
    {
      id: "settings",
      title: "Settings",
      desc: "Platform info & admins",
      icon: Settings,
      color: "from-slate-500/20 to-slate-700/10 text-slate-300 border-slate-500/30",
    },
  ];

  return (
    <div className="space-y-6 pb-20">
      {/* Overview Title Banner */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white">
            Command Center
          </h1>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/15 border border-sky-400/30 text-[10px] font-bold text-sky-400 uppercase tracking-wider">
            <Sparkles className="w-3 h-3 text-sky-400" />
            Live
          </span>
        </div>
        <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
          Manage your entire fitness platform.
        </p>
      </div>

      {/* 2×2 Platform Statistics Grid */}
      <section aria-labelledby="stats-heading">
        <h2 id="stats-heading" className="sr-only">Platform Statistics</h2>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
          {/* Total Members */}
          <div className="rounded-2xl p-3.5 bg-gradient-to-br from-[#091529] to-[#040C1A] border border-white/10 shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-400">Total Members</span>
              <div className="p-1.5 rounded-lg bg-sky-500/15 text-sky-400 border border-sky-400/20">
                <Users className="w-3.5 h-3.5 text-sky-400" />
              </div>
            </div>
            <div className="mt-2.5">
              {isLoadingStats ? (
                <div className="h-7 w-16 bg-white/10 rounded animate-pulse" />
              ) : (
                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  {totalMembers.toLocaleString()}
                </p>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">Registered athletes</p>
            </div>
          </div>

          {/* Active Trainers */}
          <div className="rounded-2xl p-3.5 bg-gradient-to-br from-[#091529] to-[#040C1A] border border-white/10 shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-400">Active Trainers</span>
              <div className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-400/20">
                <Dumbbell className="w-3.5 h-3.5 text-emerald-400" />
              </div>
            </div>
            <div className="mt-2.5">
              {isLoadingStats ? (
                <div className="h-7 w-12 bg-white/10 rounded animate-pulse" />
              ) : (
                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  {activeTrainers.toLocaleString()}
                </p>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">Verified coaches</p>
            </div>
          </div>

          {/* Workout Sessions */}
          <div className="rounded-2xl p-3.5 bg-gradient-to-br from-[#091529] to-[#040C1A] border border-white/10 shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-400">Workouts Logged</span>
              <div className="p-1.5 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-400/20">
                <Activity className="w-3.5 h-3.5 text-purple-400" />
              </div>
            </div>
            <div className="mt-2.5">
              {isLoadingStats ? (
                <div className="h-7 w-14 bg-white/10 rounded animate-pulse" />
              ) : (
                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  {workoutSessions.toLocaleString()}
                </p>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">Recorded sessions</p>
            </div>
          </div>

          {/* Upcoming Appointments */}
          <div className="rounded-2xl p-3.5 bg-gradient-to-br from-[#091529] to-[#040C1A] border border-white/10 shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-400">Appointments</span>
              <div className="p-1.5 rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-400/20">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
              </div>
            </div>
            <div className="mt-2.5">
              {isLoadingStats ? (
                <div className="h-7 w-12 bg-white/10 rounded animate-pulse" />
              ) : (
                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  {upcomingAppointments.toLocaleString()}
                </p>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">Upcoming scheduled</p>
            </div>
          </div>
        </div>
      </section>

      {/* ACTION NEEDED — HIGH PRIORITY */}
      <section aria-labelledby="action-needed-heading" className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 id="action-needed-heading" className="text-sm font-bold uppercase tracking-wider text-white">
              Action Needed
            </h2>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                totalActionCount > 0
                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                  : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
              }`}
            >
              {totalActionCount}
            </span>
          </div>
          {totalActionCount > 0 && (
            <span className="text-[11px] text-amber-400 font-medium">Requires review</span>
          )}
        </div>

        {totalActionCount === 0 ? (
          /* Empty state: All caught up */
          <div className="rounded-2xl p-4 bg-[#071326] border border-emerald-500/20 flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-bold text-white">All Caught Up!</p>
              <p className="text-[11px] text-slate-400">
                Nothing needs your attention right now.
              </p>
            </div>
          </div>
        ) : (
          /* Action rows */
          <div className="space-y-2">
            {actionItems
              .filter((item) => item.count > 0)
              .map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onNavigate(item.targetSection, item.targetFilter)}
                    className="w-full flex items-center justify-between p-3 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-amber-500/30 hover:border-amber-400/60 active:scale-[0.99] transition-all cursor-pointer text-left shadow-sm"
                    data-testid={`action-needed-${item.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                        <Icon className="w-4 h-4 text-amber-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-white truncate">{item.title}</p>
                          <span className="px-1.5 py-0.2 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold">
                            {item.count}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 truncate">{item.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-400 shrink-0 ml-2" />
                  </button>
                );
              })}
          </div>
        )}
      </section>

      {/* MANAGE PLATFORM — 2-COLUMN MANAGEMENT GRID */}
      <section aria-labelledby="manage-heading" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="manage-heading" className="text-sm font-bold uppercase tracking-wider text-white">
            Manage Platform
          </h2>
          <span className="text-[11px] text-slate-400">8 sections</span>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
          {PLATFORM_TILES.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                type="button"
                onClick={() => onNavigate(tile.id)}
                className="flex flex-col justify-between p-3.5 rounded-2xl bg-gradient-to-br from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/50 active:scale-[0.98] transition-all duration-200 text-left group cursor-pointer shadow-md"
                data-testid={`platform-tile-${tile.id}`}
              >
                <div className="flex items-center justify-between w-full mb-3">
                  <div className={`p-2 rounded-xl bg-gradient-to-br border ${tile.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-0.5 transition-all" />
                </div>
                <div>
                  <p className="text-xs font-bold text-white group-hover:text-sky-300 transition-colors">
                    {tile.title}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5 leading-snug line-clamp-1">
                    {tile.desc}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
